import {
  AssignmentStatus,
  ProjectMilestoneStatus,
  type ProjectMilestone,
  type ProjectOutcome,
  type ServiceAssignment,
} from '../graphql/types';
import type { ServiceResolutionMap } from './serviceResolution';

export interface OutcomeDistributionEntry {
  outcome: ProjectOutcome;
  count: number;
}

export interface MilestoneStatusCounts {
  milestoneId: string;
  planned: number;
  in_progress: number;
  done: number;
}

export interface ProjectKpis {
  milestonesComplete: number;
  milestonesTotal: number;
  servicesAssigned: number;
  assignmentsDone: number;
  pctDone: number;
}

export function outcomeDistribution(
  assignments: ServiceAssignment[],
  outcomes: ProjectOutcome[]
): OutcomeDistributionEntry[] {
  const counts = new Map<string, number>();
  for (const a of assignments) {
    counts.set(a.outcomeId, (counts.get(a.outcomeId) ?? 0) + 1);
  }
  return [...outcomes]
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map((outcome) => ({ outcome, count: counts.get(outcome.entityId) ?? 0 }));
}

export function statusByMilestone(
  assignments: Record<string, ServiceAssignment[]>,
  milestones: ProjectMilestone[]
): MilestoneStatusCounts[] {
  return [...milestones]
    .sort((a, b) => a.sequence - b.sequence)
    .map((milestone) => {
      const list = assignments[milestone.entityId] ?? [];
      return {
        milestoneId: milestone.entityId,
        planned: list.filter((a) => a.assignmentStatus === AssignmentStatus.Planned).length,
        in_progress: list.filter((a) => a.assignmentStatus === AssignmentStatus.InProgress).length,
        done: list.filter((a) => a.assignmentStatus === AssignmentStatus.Done).length,
      };
    });
}

/** Team key used to bucket assignments whose service has no team, or whose
 * serviceId cannot be resolved to a Service at all. Distinct from a real
 * `Team.entityId` so it can never collide with an actual team. */
export const UNASSIGNED_TEAM_KEY = '__unassigned__';
export const UNASSIGNED_TEAM_NAME = 'Unassigned';

export interface TeamLabel {
  teamKey: string;
  teamName: string;
}

export interface MilestoneTeamCounts {
  milestoneId: string;
  /** teamKey (Team.entityId, or UNASSIGNED_TEAM_KEY) -> assignment count */
  counts: Record<string, number>;
}

export interface MilestoneAndTeamCounts {
  milestoneCounts: MilestoneTeamCounts[];
  /** Distinct teams observed across all milestones, sorted by name, with
   * "Unassigned" always last. Drives the chart's legend + color scale. */
  teams: TeamLabel[];
}

/**
 * Aggregate assignment counts per milestone, segmented by the assigned
 * service's owning team (joined via the shared serviceResolution map from
 * issue #495) instead of by assignment status. Services with no team, or
 * whose serviceId is not present in the resolution map, are bucketed under
 * `UNASSIGNED_TEAM_KEY` so the chart can render a readable "Unassigned"
 * segment rather than dropping those assignments.
 */
export function statusByMilestoneAndTeam(
  assignments: Record<string, ServiceAssignment[]>,
  milestones: ProjectMilestone[],
  serviceResolution: ServiceResolutionMap
): MilestoneAndTeamCounts {
  const teamNameByKey = new Map<string, string>();

  const milestoneCounts = [...milestones]
    .sort((a, b) => a.sequence - b.sequence)
    .map((milestone) => {
      const list = assignments[milestone.entityId] ?? [];
      const counts: Record<string, number> = {};
      for (const assignment of list) {
        const resolved = serviceResolution.get(assignment.serviceId);
        const teamKey = resolved?.teamId ?? UNASSIGNED_TEAM_KEY;
        const teamName = resolved?.teamName ?? UNASSIGNED_TEAM_NAME;
        // teamKey is a Team.entityId (or the UNASSIGNED_TEAM_KEY constant) from
        // the trusted serviceResolution map — not raw external input.
        // eslint-disable-next-line security/detect-object-injection
        counts[teamKey] = (counts[teamKey] ?? 0) + 1;
        if (!teamNameByKey.has(teamKey)) {
          teamNameByKey.set(teamKey, teamName);
        }
      }
      return { milestoneId: milestone.entityId, counts };
    });

  const teams = [...teamNameByKey.entries()]
    .map(([teamKey, teamName]) => ({ teamKey, teamName }))
    .sort((a, b) => {
      if (a.teamKey === UNASSIGNED_TEAM_KEY) return 1;
      if (b.teamKey === UNASSIGNED_TEAM_KEY) return -1;
      return a.teamName.localeCompare(b.teamName);
    });

  return { milestoneCounts, teams };
}

export interface MilestoneOutcomeCountEntry {
  outcome: ProjectOutcome;
  count: number;
}

export interface MilestoneOutcomeCounts {
  milestoneId: string;
  /** Total assigned services for this milestone, including assignments whose
   * outcomeId does not resolve to a known outcome (orphan refs) — same
   * "count everything" convention as `projectKpis`. */
  total: number;
  /** Non-zero outcome counts only, sorted by `ProjectOutcome.displayOrder`.
   * Orphan outcomeId refs are excluded here (same convention as
   * `outcomeDistribution`) since there is no outcome to label them with. */
  breakdown: MilestoneOutcomeCountEntry[];
}

/**
 * Aggregate assigned-service counts per milestone, broken down by outcome
 * (issue #497). Derives entirely from the `assignments` map already fetched
 * by `useProjectAssignments` — no additional GraphQL round-trip.
 */
export function countsByMilestoneAndOutcome(
  assignments: Record<string, ServiceAssignment[]>,
  milestones: ProjectMilestone[],
  outcomes: ProjectOutcome[]
): MilestoneOutcomeCounts[] {
  const sortedOutcomes = [...outcomes].sort((a, b) => a.displayOrder - b.displayOrder);

  return [...milestones]
    .sort((a, b) => a.sequence - b.sequence)
    .map((milestone) => {
      const list = assignments[milestone.entityId] ?? [];
      const counts = new Map<string, number>();
      for (const assignment of list) {
        counts.set(assignment.outcomeId, (counts.get(assignment.outcomeId) ?? 0) + 1);
      }
      const breakdown = sortedOutcomes
        .map((outcome) => ({ outcome, count: counts.get(outcome.entityId) ?? 0 }))
        .filter((entry) => entry.count > 0);
      return { milestoneId: milestone.entityId, total: list.length, breakdown };
    });
}

export function projectKpis(
  milestones: ProjectMilestone[],
  assignments: Record<string, ServiceAssignment[]>
): ProjectKpis {
  const milestonesTotal = milestones.length;
  const milestonesComplete = milestones.filter(
    (m) => m.status === ProjectMilestoneStatus.Complete
  ).length;

  const allAssignments = Object.values(assignments).flat();
  const servicesAssigned = new Set(allAssignments.map((a) => a.serviceId)).size;
  const assignmentsDone = allAssignments.filter(
    (a) => a.assignmentStatus === AssignmentStatus.Done
  ).length;
  const pctDone =
    allAssignments.length === 0 ? 0 : Math.round((assignmentsDone / allAssignments.length) * 100);

  return { milestonesComplete, milestonesTotal, servicesAssigned, assignmentsDone, pctDone };
}
